import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

interface FlipOptions {
  /** Animation duration in milliseconds (default: 200) */
  duration?: number;
  /** Duration for exit animations. Falls back to `duration` if omitted. */
  exitDuration?: number;
  /** Delay before entry animations start in ms (default: 0) */
  delay?: number;
  /** Delay before exit animations start in ms. Falls back to `delay` if omitted. */
  exitDelay?: number;
  /** CSS easing function (default: 'ease-out') */
  easing?: string;
  /** Animate position changes via transform (default: true) */
  animatePosition?: boolean;
  /** Animate vertical position changes (default: true). Set false to lock Y axis. */
  animateY?: boolean;
  /** Animate height changes (default: false) */
  animateHeight?: boolean;
}

/**
 * FLIP animation hook — smoothly animates an element's position/size when
 * the layout changes due to a dependency update.
 *
 * **FLIP** stands for **F**irst → **L**ast → **I**nvert → **P**lay:
 * 1. Record the element's rect *before* the layout change (First).
 * 2. Let React update the DOM — the element jumps to its new rect (Last).
 * 3. Compute the delta and apply a `transform` that puts the element back
 *    at its old position (Invert).
 * 4. Animate the transform to identity so the element glides to its new
 *    home (Play).
 *
 * Uses the **Web Animations API** (`element.animate()`) for glitch-free
 * transitions. `fill: 'backwards'` holds the old visual state during any
 * delay period, eliminating the flash that CSS-transition-based FLIP
 * implementations can produce.
 *
 * @see https://aerotwist.com/blog/flip-your-animations/ — original FLIP technique
 */
export function useLayoutFlip(
  ref: RefObject<HTMLElement | null>,
  deps: unknown[],
  options: FlipOptions = {},
) {
  const {
    duration = 200,
    exitDuration,
    delay = 0,
    exitDelay,
    easing = 'ease-out',
    animatePosition = true,
    animateY = true,
    animateHeight = false,
  } = options;

  const prevRect = useRef<DOMRect | null>(null);
  const isFirstRender = useRef(true);
  const animating = useRef(false);

  // ---------- FIRST ----------
  // Record rect after every paint so we always have the "before" snapshot.
  // Skip while a FLIP animation is running to avoid reading mid-animation rect.
  useEffect(() => {
    if (animating.current || !ref.current) return;
    prevRect.current = ref.current.getBoundingClientRect();
  });

  // ---------- LAST → INVERT → PLAY ----------
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dep array forwarding
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !prevRect.current || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const newRect = el.getBoundingClientRect();
    const deltaX = prevRect.current.left - newRect.left;
    const deltaY = animateY ? prevRect.current.top - newRect.top : 0;
    const deltaH = prevRect.current.height - newRect.height;

    const hasMoved =
      animatePosition && (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1);
    const hasResized = animateHeight && Math.abs(deltaH) > 2;

    if (!hasMoved && !hasResized) return;

    // "Exit" = element returning towards rest (moving right/down or shrinking).
    const isExit = deltaX < 0 || deltaY < 0 || (hasResized && deltaH > 0);

    const ms = isExit && exitDuration != null ? exitDuration : duration;
    const delayMs = isExit && exitDelay != null ? exitDelay : delay;

    animating.current = true;

    // Build keyframes: from (old position) → to (new/current position).
    const from: Record<string, string> = {};
    const to: Record<string, string> = {};

    if (hasMoved) {
      from.transform = `translate(${deltaX}px, ${deltaY}px)`;
      to.transform = 'translate(0, 0)';
    }
    if (hasResized) {
      from.height = `${prevRect.current!.height}px`;
      to.height = `${newRect.height}px`;
    }

    // Web Animations API — single atomic operation, no reflow hacks needed.
    // `fill: 'backwards'` holds the "from" keyframe during the delay period,
    // preventing any flash of the element at its new position.
    const animation = el.animate([from, to], {
      duration: ms,
      delay: delayMs,
      easing,
      fill: 'backwards',
    });

    animation.onfinish = () => {
      animating.current = false;
      prevRect.current = el.getBoundingClientRect();
    };

    return () => {
      animation.cancel();
      animating.current = false;
    };
  }, deps);
}
